import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Tooltip } from "@mui/material";
import { DatePicker } from "antd";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { getEmployeeAttendanceDetail } from "../../services/attendanceReport.service";
import Styles from "./AttendanceReport.module.scss";

const AttendanceReportDetail = ({ userId, userInfo, dateRange: initialDateRange, onBack }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState(initialDateRange);
    const [statusCounts, setStatusCounts] = useState({ P: 0, LOP: 0, HD: 0, MP: 0, WO: 0, H: 0, L: 0 });

    const columns = [
        { id: 'date', label: 'Date', numeric: false },
        { id: 'expectedInTime', label: 'Expected In', numeric: false },
        { id: 'actualInTime', label: 'Actual In', numeric: false },
        { id: 'expectedOutTime', label: 'Expected Out', numeric: false },
        { id: 'actualOutTime', label: 'Actual Out', numeric: false },
        { id: 'workHours', label: 'Work Time', numeric: false },
        { id: 'actualTaskWorkedHours', label: 'Tasksheet Time', numeric: false },
        { id: 'status', label: 'Status', numeric: false },
    ];

    const detailTableConfig = {
        isAction: false,
        isView: false,
    };

    useEffect(() => {
        if (userId && dateRange) {
            fetchDetail();
        }
    }, [userId, dateRange]);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const params = {
                startDate: dateRange[0].format('YYYY-MM-DD'),
                endDate: dateRange[1].format('YYYY-MM-DD')
            };
            const res = await getEmployeeAttendanceDetail(userId, params);
            if (res) {
                const counts = { P: 0, LOP: 0, HD: 0, MP: 0, WO: 0, H: 0, L: 0, CompOff: 0 };

                const formattedData = (res.data || []).map(item => {
                    const statusVal = (item.status === "FULL LOP" || item.status === "Full LOP") ? "LOP" : item.status;

                    if (statusVal === 'P' || statusVal === 'Present') counts.P++;
                    else if (statusVal === 'LOP' || statusVal === 'A') counts.LOP++;
                    else if (statusVal === 'HALF LOP' || statusVal === 'Half LOP' || statusVal === 'Half Day') counts.HD++;
                    else if (statusVal === 'Miss Punch' || statusVal === 'MP') counts.MP++;
                    else if (statusVal === 'WO' || statusVal === 'Weekly Off') counts.WO++;
                    else if (statusVal === 'H' || statusVal === 'Holiday') counts.H++;
                    else if (statusVal === 'L' || statusVal === 'Leave') counts.L++;
                    else if (statusVal === 'CompOff' || statusVal === 'Compoff') counts.CompOff++;

                    // Define styles for each status
                    let statusStyle = {
                        fontWeight: '600',
                        fontSize: '12px',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        minWidth: '30px',
                        textAlign: 'center',
                    };

                    switch (statusVal) {
                        case 'P':
                        case 'Present':
                            statusStyle = { ...statusStyle, color: '#13c2c2', backgroundColor: '#e6fffb', border: '1px solid #87e8de' }; // Cyan/Greenish
                            break;
                        case 'WO':
                            statusStyle = { ...statusStyle, color: '#00000073', backgroundColor: '#f5f5f5', border: '1px solid #d9d9d9' }; // Grey
                            break;
                        case 'LOP':
                        case 'A':
                            statusStyle = { ...statusStyle, color: '#ff4d4f', backgroundColor: '#fff1f0', border: '1px solid #ffa39e' }; // Red
                            break;
                        case 'L':
                        case 'Leave':
                            statusStyle = { ...statusStyle, color: '#fa8c16', backgroundColor: '#fff7e6', border: '1px solid #ffd591' }; // Orange
                            break;
                        case 'H':
                        case 'Holiday':
                            statusStyle = { ...statusStyle, color: '#722ed1', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }; // Purple
                            break;
                        case 'CompOff':
                        case 'Compoff':
                            statusStyle = { ...statusStyle, color: '#096dd9', backgroundColor: '#e6f7ff', border: '1px solid #91caff' }; // Blue
                            break;
                        case 'HALF LOP':
                        case 'Half LOP':
                            statusStyle = { ...statusStyle, color: '#c41d7f', backgroundColor: '#fff0f6', border: '1px solid #ffadd2' }; // Pink
                            break;
                        default:
                            statusStyle = { ...statusStyle, color: '#595959ff', backgroundColor: '#fafafa', border: '1px solid #f0f0f0' };
                            break;
                    }

                    // Tooltip Content logic
                    let tooltipContent = "";
                    if (item.requested === "Reverted" || item.requested === "Requested") {
                        // Show notes and type properly
                        tooltipContent = (
                            <Box>
                                <Typography variant="body2" sx={{ fontSize: '12px' }}>Status: {item.requested === "Requested" ? "Pending" : "Approved"}</Typography>
                                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 'bold' }}>Type: {item.type}</Typography>
                                <Typography variant="body2" sx={{ fontSize: '12px' }}>Notes: {item.notes}</Typography>
                            </Box>
                        );
                    }

                    const getTimeOnDate = (date, timeSource) => {
                        const d = dayjs(date);
                        const t = dayjs(timeSource);
                        return d.hour(t.hour()).minute(t.minute()).second(t.second());
                    };

                    let isLateIn = false;
                    if (item.actualInTime && item.expectedInTime) {
                        const expectedIn = getTimeOnDate(item.actualInTime, item.expectedInTime);
                        isLateIn = dayjs(item.actualInTime).isAfter(expectedIn);
                    }

                    let isEarlyOut = false;
                    if (item.actualOutTime && item.expectedOutTime) {
                        const expectedOut = getTimeOnDate(item.actualOutTime, item.expectedOutTime);
                        isEarlyOut = dayjs(item.actualOutTime).isBefore(expectedOut);
                    }

                    return {
                        ...item,
                        date: dayjs(item.date).format('DD MMM YYYY'), // Matches image format "22 Jan 2026"
                        expectedInTime: formatTime(item.expectedInTime),
                        actualInTime: (
                            <Typography sx={{ color: isLateIn ? 'red' : 'inherit', fontSize: '13px' }}>
                                {formatTime(item.actualInTime)}
                            </Typography>
                        ),
                        expectedOutTime: formatTime(item.expectedOutTime),
                        actualOutTime: (
                            <Typography sx={{ color: isEarlyOut ? 'red' : 'inherit', fontSize: '13px' }}>
                                {formatTime(item.actualOutTime)}
                            </Typography>
                        ),
                        workHours: formatDuration(item.workHours),
                        actualTaskWorkedHours: formatDuration(item.actualTaskWorkedHours),
                        status: (
                            <Tooltip title={tooltipContent} arrow placement="top">
                                <Box sx={statusStyle}>
                                    {statusVal}
                                </Box>
                            </Tooltip>
                        ),
                    };
                });
                setData(formattedData);
                setStatusCounts(counts);
            }
        } catch (error) {
            console.error("Error fetching Detail:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (time) => {
        if (!time) return "-";
        return dayjs(time).format("hh:mm A");
    }

    const formatDuration = (seconds) => {
        if (!seconds) return "-";
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    return (
        <>
            <Box className={Styles.detailCard}>
                {/* ... Header Section ... */}
                <Box className={Styles.userHeader} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 1, md: 0 } }}>
                    <Box className={Styles.backButton} onClick={onBack}>
                        <ArrowBackIcon fontSize="small" />
                        <Typography variant="body1">Back to Summary</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, md: 3 }, alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, mt: { xs: 1, md: 0 } }}>
                        {/* ... User Infos ... */}
                        <Box className={Styles.userInfo}>
                            <label>Employee Name</label>
                            <span>{userInfo?.userName}</span>
                        </Box>
                        <Box className={Styles.userInfo}>
                            <label>Shift</label>
                            <span>{userInfo?.shift}</span>
                        </Box>
                        <Box className={Styles.userInfo}>
                            <label>Working Count</label>
                            <span>{userInfo?.totalWorkingCount || 0}</span>
                        </Box>
                        <Box className={Styles.userInfo} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <label>Date Range</label>
                            <DatePicker.RangePicker
                                format="DD MMM YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                                style={{ width: '100%', maxWidth: 250 }}
                                allowClear={false}
                                placement="bottomRight"
                                getPopupContainer={(trigger) => trigger.parentElement}
                            />
                        </Box>
                    </Box>

                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, mt: 1 }}>
                    {[
                        { label: 'Present', count: statusCounts.P, color: '#13c2c2', bg: '#e6fffb' }, // Cyan
                        { label: 'Absent', count: statusCounts.LOP, color: '#ff4d4f', bg: '#fff1f0' }, // Red (LOP/Absent)
                        { label: 'Half Day', count: statusCounts.HD, color: '#ca4886', bg: '#fff0f6' }, // Pink
                        { label: 'Weekly Off', count: statusCounts.WO, color: '#595959', bg: '#f5f5f5' }, // Grey
                        { label: 'Holiday', count: statusCounts.H, color: '#722ed1', bg: '#f9f0ff' }, // Purple
                        { label: 'Comp Off', count: statusCounts.CompOff, color: '#096dd9', bg: '#e6f7ff' }, // Blue
                        { label: 'Leave', count: statusCounts.L, color: '#fa8c16', bg: '#fff7e6' }, // Orange
                    ].map((chip) => (
                        <Box
                            key={chip.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: '1px solid #f0f0f0',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                fontSize: '13px',
                                height: '32px'
                            }}
                        >
                            <Box sx={{ px: 1.5, py: 0.5, backgroundColor: '#fff', color: '#595959', fontWeight: 500 }}>
                                {chip.label}
                            </Box>
                            <Box sx={{ px: 1.5, py: 0.5, backgroundColor: chip.bg, color: chip.count > 0 ? chip.color : '#bfbfbf', fontWeight: 700, minWidth: '30px', textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {chip.count}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Summary Chips */}

            <MasterCommonTable
                tableConfig={detailTableConfig}
                columns={columns}
                data={data}
                isPaginationBar={false}
                tableTotalCount={data.length}
                loading={loading}
                getRowStyle={(row) => {
                    if (row.requested === "Requested") {
                        return { backgroundColor: "#e7ffd3ff" }; // Light Green for "request"
                    } else if (row.requested === "Reverted") {
                        return { backgroundColor: "#ffe3e1ff" }; // Light Red for "Reverted"
                    }
                    return {};
                }}
            />
        </>
    );
};

export default AttendanceReportDetail;
